import type { ECharts } from 'echarts';

export type EchartsLineTooltipValue = number | null | undefined;

/**
 * Describes one visible ECharts line series that should use the wider tooltip hit area.
 */
export interface EchartsLineTooltipSeries {
  name: string;
  seriesIndex: number;
  data: EchartsLineTooltipValue[];
  /**
   * X-axis values as ECharts receives them. Category axes should pass labels, not indexes.
   */
  categories: Array<string | number>;
  connectNulls?: boolean;
}

export interface EchartsLineTooltipAxes {
  xAxisIndex?: number;
  yAxisIndex?: number;
}

export interface EchartsLineTooltipMouseMoveOptions {
  chart: ECharts | null;
  /**
   * Mouse position relative to the ECharts container, usually [offsetX, offsetY].
   */
  pointInPixel: number[];
  series: EchartsLineTooltipSeries[];
  dataLength: number;
  hitDistance?: number;
  axes?: EchartsLineTooltipAxes;
  /**
   * Optional legend-aware guard. Return false when a series is hidden.
   */
  isSeriesSelected?: (seriesName: string) => boolean;
}

export interface EchartsLineTooltipDataIndexOptions {
  chart: ECharts | null;
  pointInPixel: number[];
  dataLength: number;
  axes?: EchartsLineTooltipAxes;
}

interface EchartsLineTooltipHover {
  seriesIndex: number;
  dataIndex: number;
  distance: number;
}

export const DEFAULT_ECHARTS_LINE_TOOLTIP_HIT_DISTANCE = 18;

/**
 * Adds Apex-like line hover behavior to ECharts charts without changing their visible series.
 *
 * ECharts item tooltips on line charts are usually easy to trigger only on symbols.
 * This controller checks the mouse distance to rendered line segments, then dispatches
 * the same showTip action ECharts would use when hovering a real data point.
 */
export class EchartsLineTooltipController {
  /**
   * Used by tooltip formatters when a manual line hover does not provide params.dataIndex.
   */
  hoveredDataIndex: number | null = null;

  private isTooltipVisible = false;
  private tooltipFrame: number | null = null;

  handleMouseMove(options: EchartsLineTooltipMouseMoveOptions): number | null {
    // Keep a best-effort category index even when the cursor is not close enough to a line.
    this.hoveredDataIndex = this.getDataIndexFromPoint(options);

    const lineHover = this.getLineTooltipHover(options);

    if (lineHover) {
      this.hoveredDataIndex = lineHover.dataIndex;
      this.isTooltipVisible = true;
      this.showTooltip(options.chart, lineHover);

      return this.hoveredDataIndex;
    }

    if (this.isTooltipVisible) {
      this.hideTooltip(options.chart);
    }

    return this.hoveredDataIndex;
  }

  getTooltipDataIndex(params: unknown): number {
    const tooltipParams = Array.isArray(params) ? params[0] : params;
    const rawDataIndex = (tooltipParams as { dataIndex?: number } | undefined)?.dataIndex;
    const dataIndex = Number(rawDataIndex);

    // ECharts point/bar hover params include dataIndex. Manual line-path tooltip calls may not.
    if (rawDataIndex !== null && rawDataIndex !== undefined && Number.isFinite(dataIndex)) {
      return dataIndex;
    }

    return this.hoveredDataIndex ?? -1;
  }

  getDataIndexFromPoint(options: EchartsLineTooltipDataIndexOptions): number | null {
    const { chart, pointInPixel, dataLength } = options;

    if (!chart || dataLength === 0 || !chart.containPixel('grid', pointInPixel)) {
      return null;
    }

    const [xIndex] = chart.convertFromPixel(this.getAxes(options.axes), pointInPixel) as number[];
    const nearestIndex = Math.round(Number(xIndex));

    if (!Number.isFinite(nearestIndex)) {
      return null;
    }

    return Math.max(0, Math.min(nearestIndex, dataLength - 1));
  }

  hideTooltip(chart: ECharts | null): void {
    this.isTooltipVisible = false;
    this.cancelTooltipFrame();
    chart?.dispatchAction({ type: 'hideTip' });
  }

  reset(): void {
    this.hoveredDataIndex = null;
    this.isTooltipVisible = false;
    this.cancelTooltipFrame();
  }

  destroy(chart: ECharts | null): void {
    this.reset();
    chart?.dispatchAction({ type: 'hideTip' });
  }

  private showTooltip(chart: ECharts | null, lineHover: EchartsLineTooltipHover): void {
    this.cancelTooltipFrame();

    if (typeof window === 'undefined') {
      this.dispatchShowTooltip(chart, lineHover);
      return;
    }

    // Let ECharts finish its own mousemove work first, otherwise it can immediately hide our manual tooltip.
    this.tooltipFrame = window.requestAnimationFrame(() => {
      this.tooltipFrame = null;

      if (!this.isTooltipVisible || this.hoveredDataIndex !== lineHover.dataIndex) {
        return;
      }

      this.dispatchShowTooltip(chart, lineHover);
    });
  }

  private dispatchShowTooltip(chart: ECharts | null, lineHover: EchartsLineTooltipHover): void {
    chart?.dispatchAction({
      type: 'showTip',
      seriesIndex: lineHover.seriesIndex,
      dataIndex: lineHover.dataIndex
    });
  }

  private cancelTooltipFrame(): void {
    if (this.tooltipFrame === null || typeof window === 'undefined') {
      this.tooltipFrame = null;
      return;
    }

    window.cancelAnimationFrame(this.tooltipFrame);
    this.tooltipFrame = null;
  }

  private getLineTooltipHover(options: EchartsLineTooltipMouseMoveOptions): EchartsLineTooltipHover | null {
    const { chart, pointInPixel, series, hitDistance = DEFAULT_ECHARTS_LINE_TOOLTIP_HIT_DISTANCE } = options;

    if (!chart || !chart.containPixel('grid', pointInPixel)) {
      return null;
    }

    const nearestDataIndex = this.getDataIndexFromPoint(options);
    let closestHover: EchartsLineTooltipHover | null = null;

    // Find the closest rendered point or line segment across all active line series.
    for (const lineSeries of series) {
      if (options.isSeriesSelected?.(lineSeries.name) === false) {
        continue;
      }

      let previousPoint: { dataIndex: number; pixel: number[] } | null = null;

      for (let dataIndex = 0; dataIndex < lineSeries.data.length; dataIndex++) {
        const value = lineSeries.data[dataIndex];

        if (value === null || value === undefined) {
          // Match ECharts line breaks unless the source series connects nulls.
          if (!lineSeries.connectNulls) {
            previousPoint = null;
          }
          continue;
        }

        const currentPixel = this.getDataPointPixel(chart, dataIndex, value, lineSeries.categories, options.axes);

        if (!currentPixel) {
          continue;
        }

        const pointHover = {
          seriesIndex: lineSeries.seriesIndex,
          dataIndex,
          distance: this.getPointDistance(pointInPixel, currentPixel)
        };

        if (!closestHover || pointHover.distance < closestHover.distance) {
          closestHover = pointHover;
        }

        if (previousPoint) {
          const segmentDistance = this.getSegmentDistance(pointInPixel, previousPoint.pixel, currentPixel);
          // When hovering between points, show the tooltip for the nearest x-axis category.
          const projectedDataIndex =
            nearestDataIndex !== null && lineSeries.data[nearestDataIndex] !== null
              ? nearestDataIndex
              : this.getNearestSegmentEndpointIndex(pointInPixel, previousPoint, { dataIndex, pixel: currentPixel });
          const segmentHover = {
            seriesIndex: lineSeries.seriesIndex,
            dataIndex: projectedDataIndex,
            distance: segmentDistance
          };

          if (!closestHover || segmentHover.distance < closestHover.distance) {
            closestHover = segmentHover;
          }
        }

        previousPoint = { dataIndex, pixel: currentPixel };
      }
    }

    if (!closestHover || closestHover.distance > hitDistance) {
      return null;
    }

    return closestHover;
  }

  private getDataPointPixel(
    chart: ECharts,
    dataIndex: number,
    value: number,
    categories: Array<string | number>,
    axes?: EchartsLineTooltipAxes
  ): number[] | null {
    const xAxisValue = categories[dataIndex];

    if (xAxisValue === null || xAxisValue === undefined) {
      return null;
    }

    // Category axes need their real labels here; numeric indexes can miss the rendered line geometry.
    const pixel = chart.convertToPixel(this.getAxes(axes), [xAxisValue, value]) as number[];

    if (!Array.isArray(pixel) || pixel.some((coordinate) => !Number.isFinite(Number(coordinate)))) {
      return null;
    }

    return pixel;
  }

  private getNearestSegmentEndpointIndex(
    pointInPixel: number[],
    startPoint: { dataIndex: number; pixel: number[] },
    endPoint: { dataIndex: number; pixel: number[] }
  ): number {
    const startDistance = this.getPointDistance(pointInPixel, startPoint.pixel);
    const endDistance = this.getPointDistance(pointInPixel, endPoint.pixel);

    return startDistance <= endDistance ? startPoint.dataIndex : endPoint.dataIndex;
  }

  private getPointDistance(pointA: number[], pointB: number[]): number {
    return Math.hypot(pointA[0] - pointB[0], pointA[1] - pointB[1]);
  }

  private getSegmentDistance(point: number[], start: number[], end: number[]): number {
    const segmentX = end[0] - start[0];
    const segmentY = end[1] - start[1];
    const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

    if (segmentLengthSquared === 0) {
      return this.getPointDistance(point, start);
    }

    // Project the mouse onto the finite segment and measure the distance to that projected point.
    const projection = Math.max(
      0,
      Math.min(1, ((point[0] - start[0]) * segmentX + (point[1] - start[1]) * segmentY) / segmentLengthSquared)
    );
    const projectedPoint = [start[0] + projection * segmentX, start[1] + projection * segmentY];

    return this.getPointDistance(point, projectedPoint);
  }

  private getAxes(axes?: EchartsLineTooltipAxes): Required<EchartsLineTooltipAxes> {
    return {
      xAxisIndex: axes?.xAxisIndex ?? 0,
      yAxisIndex: axes?.yAxisIndex ?? 0
    };
  }
}
